import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RetentionCampaign } from '@flipdish/api-client-typescript';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpansionPanel from '@mui/material/Accordion';
import ExpansionPanelDetails from '@mui/material/AccordionDetails';
import ExpansionPanelSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { type FormikProps, Form, withFormik } from 'formik';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import YesNoDialog from '@fd/ui/Dialog/YesNoDialog';
import { FkNumeric } from '@fd/ui/FkNumeric/FkNumeric';
import FkSelect from '@fd/ui/FkSelect/FkSelect';
import { FkSwitch } from '@fd/ui/FkSwitch/FkSwitch';
import FormItemLayout from '@fd/ui/Forms/FormItemLayout';
import { Modal } from '@fd/ui/Modal/Modal';
import { ModalActions } from '@fd/ui/Modal/ModalActions';
import { ModalContent } from '@fd/ui/Modal/ModalContent';
import { OptionType } from '@fd/ui/Select/Select';

import { FIELDS } from '../../../models/fields';
import { flagService } from '../../../services';
import { getSalesChannelType } from '../../../ui/utils/generateSalesChannelsTypes';
import useStoreToSalesChannelTransformerWithQuery from '../../Settings/utils/useStoreToSalesChannelTransformerWithQuery';
import { getStores, getStoreSelectOptions } from '../selectors';
import ActionButtonGroup from './ActionButtonGroup';
import AssignStoresField from './AssignStoresField';

const useStyles = makeStyles((theme: Theme) => ({
  switch: { display: 'flex', justifyContent: 'flex-end' },
  lowerCase: { textTransform: 'lowercase' },
  removeDialog: { whiteSpace: 'pre-line' },
  modalContent: { maxHeight: '70vh', overflowY: 'auto' },
  advanced: {
    backgroundColor: '#FAFAFA',
    '&:before': { display: 'none' },
    '&::before': { display: 'none' },
  },
  discountCodeAfter: {
    alignItems: 'center',
    '&& [data-fd="campaign-notify-customer-after-select"]': { flexWrap: 'unset' },
  },
  summary: {
    '& .MuiTypography-root': { fontWeight: 500 },
    minHeight: 'unset',
    padding: theme.spacing(2, 0),
    margin: theme.spacing(1.5, 0),
    '&.Mui-expanded': {
      minHeight: 'unset',
      padding: theme.spacing(2, 0),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '&& .MuiAccordionSummary-content': { fontWeight: 500, margin: theme.spacing(0, 0) },
    '&& .MuiAccordionSummary-content.Mui-expanded': {
      fontWeight: 500,
      margin: theme.spacing(0, 0),
    },
  },
}));

type InnerProps = MappedProps & FormikProps<FormValues>;
type OuterProps = {
  editMode: boolean;
  campaignData?: RetentionCampaign;
  onCancel: () => void;
  onRemove: (CampaignId: number) => void;
  open: boolean;
  onCreate: (values: FormValues) => void;
};

type Props = InnerProps & OuterProps;

export type FormValues = ReturnType<typeof getDefaultFormState>;
export const getDefaultFormState = ({ campaignData = {} as RetentionCampaign, editMode }) => {
  const formState = {
    IsEnabled: true,
    StoreIds: [],
    NotifyCustomerAfterMinutes: 28,
    PercentDiscountAmount: 20,
    VoucherValidPeriodDays: 7,
    IncludeDeliveryFee: false,
    AutoApplyResultingVouchers: false,
    IncludeExistingOrders: false,
  } as RetentionCampaign;

  if (editMode) {
    return { ...formState, ...campaignData };
  }
  return formState;
};

type State = { storesOptions: OptionType[]; isRemoveDialogVisible: boolean };

const RetentionCampaignModal = (props: Props) => {
  const {
    allStores,
    editMode,
    initialValues,
    isLoyaltyMaxDiscountOn,
    isSimplifiedRetentionCampaignOn,
    translate,
    onCancel,
    onRemove,
  } = props;
  const classes = useStyles();
  const [state, setState] = useState<State>({ storesOptions: [], isRemoveDialogVisible: false });
  const titleTextId = editMode ? 'Modify_retention_campaign' : 'Create_new_retention_campaign';
  const hasAppliedDefaultsOnceRef = useRef(false);
  const notifyCustomerAfterMinutesOptions = [
    { value: 28, label: translate('4_weeks_recommended') as TranslationId },
    { value: 7, label: translate('1_week') as TranslationId },
    { value: 14, label: translate('2_weeks') as TranslationId },
    { value: 21, label: translate('3_weeks') as TranslationId },
    { value: 42, label: translate('6_weeks') as TranslationId },
    { value: 56, label: translate('8_weeks') as TranslationId },
    { value: 84, label: translate('12_weeks') as TranslationId },
  ];
  const showCustomMinutes = React.useMemo(
    () =>
      !notifyCustomerAfterMinutesOptions.some(
        (opt) => opt.value === props.values.NotifyCustomerAfterMinutes
      ),
    [notifyCustomerAfterMinutesOptions, props.values.NotifyCustomerAfterMinutes]
  );

  const storeIdAssociations = useStoreToSalesChannelTransformerWithQuery();
  useEffect(() => {
    const stores = allStores.filter(
      (store) =>
        initialValues.StoreIds?.indexOf(store.StoreId as number) !== -1 ||
        !store.HasRetentionCampaign
    );
    setState((prev) => ({ ...prev, storesOptions: stores.map(getStoreSelectOptions) }));
  }, [allStores, storeIdAssociations]);

  // Default eligible stores when creating (not editing) and values are empty. No ref/state.
  useEffect(() => {
    if (!isSimplifiedRetentionCampaignOn || editMode || !props.open) return;
    if (hasAppliedDefaultsOnceRef.current) return;
    if (props.dirty) return; // run only before any user edits
    if (props.touched?.StoreIds) return; // don't re-apply after user touched the field
    const currentIds = props.values.StoreIds ?? [];
    if (currentIds.length > 0) return;
    if (!allStores || allStores.length === 0) return;

    const eligibleStores = allStores.filter(
      (store) =>
        initialValues.StoreIds?.indexOf(store.StoreId as number) !== -1 ||
        !store.HasRetentionCampaign
    );
    if (eligibleStores.length === 0) return;

    const allIds = eligibleStores.map((s) => s.StoreId as number);
    props.setFieldValue('StoreIds', allIds, false);
    hasAppliedDefaultsOnceRef.current = true;
  }, [
    editMode,
    props.open,
    allStores,
    initialValues.StoreIds,
    props.values.StoreIds,
    props.dirty,
    props.touched?.StoreIds,
  ]);

  const preSelectedStores = useMemo(() => {
    const ids = props.values.StoreIds ?? [];
    if (!ids.length) return undefined;
    return allStores
      .filter((s) => ids.includes(s.StoreId as number))
      .map((s) => {
        const salesChannelInfo = storeIdAssociations.find(
          (association) => association.storeId === s.StoreId
        );
        return {
          label: `${s.StoreName as string}${getSalesChannelType(salesChannelInfo?.salesChannelType ?? '')}`,
          value: s.StoreId as number,
        } as OptionType;
      });
  }, [allStores, storeIdAssociations, props.values.StoreIds]);

  const handleRemoveClick = useCallback(() => {
    setState({ ...state, isRemoveDialogVisible: true });
  }, []);

  return (
    <>
      <Modal titleTextId={titleTextId as TranslationId} open={props.open} onClose={onCancel}>
        <Form>
          <ModalContent>
            {isSimplifiedRetentionCampaignOn ? (
              <div className={classes.modalContent}>
                <Divider />
                <FormItemLayout
                  labelId="Is_the_campaign_enabled"
                  bottomBorder
                  smProportion="10x2"
                  mdProportion="10x2"
                  removeRootPaddings={true}
                  noWrap
                >
                  <Box className={classes.switch}>
                    <FkSwitch {...FIELDS['CampaignIsEnabled']} name={'IsEnabled'} />
                  </Box>
                </FormItemLayout>
                <FormItemLayout
                  descriptionId="Retention_campaign_sales_channel"
                  labelId="Sales_channels"
                  mdProportion="12x12"
                  removeRootPaddings
                >
                  <AssignStoresField fieldName={'StoreIds'} preSelectedStores={preSelectedStores} />
                </FormItemLayout>

                <FormItemLayout
                  className={classes.discountCodeAfter}
                  labelId="Send_discount_code_after"
                  descriptionId="if_user_has_not_placed_any_orders"
                  mdProportion="8x4"
                  removeRootPaddings
                >
                  {showCustomMinutes ? (
                    <FkNumeric
                      name={'NotifyCustomerAfterMinutes'}
                      {...FIELDS['CampaignNotifyCustomerAfterMinutes']}
                      renderEndAdornmen={() => (
                        <span className={classes.lowerCase}>
                          <Translate id="Days" />
                        </span>
                      )}
                    />
                  ) : (
                    <FkSelect
                      {...FIELDS['CampaignNotifyCustomerAfterMinutes']}
                      menuPlacement="top"
                      name={'NotifyCustomerAfterMinutes'}
                      options={notifyCustomerAfterMinutesOptions}
                      removeTopPadding
                      variant="standard"
                      fdKey="campaign-notify-customer-after-select"
                    />
                  )}
                </FormItemLayout>
                <FormItemLayout
                  labelId="Amount_of_discount"
                  bottomBorder
                  mdProportion="8x4"
                  removeRootPaddings
                >
                  <FkNumeric
                    name={'PercentDiscountAmount'}
                    {...FIELDS['CampaignPercentDiscountAmount']}
                    renderEndAdornmen={() => '%'}
                  />
                </FormItemLayout>
                <ExpansionPanel elevation={0} className={classes.advanced}>
                  <ExpansionPanelSummary
                    className={classes.summary}
                    data-fd="show-advanced-options"
                    expandIcon={<ExpandMoreIcon />}
                  >
                    <Typography>
                      <Translate id="Show_advanced_options" />
                    </Typography>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails>
                    <FormItemLayout
                      labelId="Voucher_will_be_valid_for"
                      bottomBorder
                      mdProportion="8x4"
                      removeRootPaddings
                    >
                      <FkNumeric
                        name={'VoucherValidPeriodDays'}
                        {...FIELDS['CampaignVoucherValidPeriodDays']}
                        renderEndAdornmen={() => (
                          <span className={classes.lowerCase}>
                            <Translate id="Days" />
                          </span>
                        )}
                      />
                    </FormItemLayout>
                    <FormItemLayout
                      labelId="Does_it_cover_delivery_fees"
                      bottomBorder
                      smProportion="10x2"
                      mdProportion="10x2"
                      removeRootPaddings
                      noWrap
                    >
                      <Box className={classes.switch}>
                        <FkSwitch
                          {...FIELDS['CampaignIncludeDeliveryFee']}
                          name={'IncludeDeliveryFee'}
                        />
                      </Box>
                    </FormItemLayout>

                    <FormItemLayout
                      labelId="Does_it_apply_automatically_to_orders"
                      bottomBorder
                      smProportion="10x2"
                      mdProportion="10x2"
                      removeRootPaddings
                      noWrap
                    >
                      <Box className={classes.switch}>
                        <FkSwitch
                          {...FIELDS['CampaignAutomaticallyApplyExistingOrders']}
                          name={'AutoApplyResultingVouchers'}
                        />
                      </Box>
                    </FormItemLayout>

                    <FormItemLayout
                      bottomBorder
                      labelId="Force_discount"
                      descriptionId="Force_discount_description"
                      mdProportion="10x2"
                      noWrap
                      removeRootPaddings
                      smProportion="10x2"
                    >
                      <Box className={classes.switch}>
                        <FkSwitch {...FIELDS['CampaignForceDiscount']} name={'ForceDiscount'} />
                      </Box>
                    </FormItemLayout>

                    {isLoyaltyMaxDiscountOn && (
                      <FormItemLayout
                        bottomBorder
                        descriptionId="Max_discount_amount_description"
                        labelId="Max_discount_amount"
                        mdProportion="8x4"
                        removeRootPaddings
                      >
                        <FkNumeric {...FIELDS['CampaignMaxDiscount']} name={'MaxDiscount'} />
                      </FormItemLayout>
                    )}
                  </ExpansionPanelDetails>
                </ExpansionPanel>
              </div>
            ) : (
              <>
                <Divider />
                <FormItemLayout
                  labelId="Is_the_campaign_enabled"
                  bottomBorder
                  smProportion="10x2"
                  mdProportion="10x2"
                  removeRootPaddings={true}
                  noWrap
                >
                  <Box className={classes.switch}>
                    <FkSwitch {...FIELDS['CampaignIsEnabled']} name={'IsEnabled'} />
                  </Box>
                </FormItemLayout>

                <FormItemLayout
                  descriptionId="Retention_campaign_sales_channel"
                  labelId="Sales_channels"
                  mdProportion="12x12"
                  removeRootPaddings
                >
                  <AssignStoresField fieldName={'StoreIds'} preSelectedStores={preSelectedStores} />
                </FormItemLayout>

                <FormItemLayout
                  labelId="Send_discount_code_after"
                  bottomBorder
                  descriptionId="if_user_has_not_placed_any_orders"
                  mdProportion="8x4"
                  removeRootPaddings
                >
                  <FkNumeric
                    name={'NotifyCustomerAfterMinutes'}
                    {...FIELDS['CampaignNotifyCustomerAfterMinutes']}
                    renderEndAdornmen={() => (
                      <span className={classes.lowerCase}>
                        <Translate id="Days" />
                      </span>
                    )}
                  />
                </FormItemLayout>

                <FormItemLayout
                  labelId="Amount_of_discount"
                  bottomBorder
                  mdProportion="8x4"
                  removeRootPaddings
                >
                  <FkNumeric
                    name={'PercentDiscountAmount'}
                    {...FIELDS['CampaignPercentDiscountAmount']}
                    renderEndAdornmen={() => '%'}
                  />
                </FormItemLayout>

                <FormItemLayout
                  labelId="Voucher_will_be_valid_for"
                  bottomBorder
                  mdProportion="8x4"
                  removeRootPaddings
                >
                  <FkNumeric
                    name={'VoucherValidPeriodDays'}
                    {...FIELDS['CampaignVoucherValidPeriodDays']}
                    renderEndAdornmen={() => (
                      <span className={classes.lowerCase}>
                        <Translate id="Days" />
                      </span>
                    )}
                  />
                </FormItemLayout>

                <FormItemLayout
                  labelId="Does_it_cover_delivery_fees"
                  bottomBorder
                  smProportion="10x2"
                  mdProportion="10x2"
                  removeRootPaddings
                  noWrap
                >
                  <Box className={classes.switch}>
                    <FkSwitch
                      {...FIELDS['CampaignIncludeDeliveryFee']}
                      name={'IncludeDeliveryFee'}
                    />
                  </Box>
                </FormItemLayout>

                <FormItemLayout
                  labelId="Does_it_apply_automatically_to_orders"
                  bottomBorder
                  smProportion="10x2"
                  mdProportion="10x2"
                  removeRootPaddings
                  noWrap
                >
                  <Box className={classes.switch}>
                    <FkSwitch
                      {...FIELDS['CampaignAutomaticallyApplyExistingOrders']}
                      name={'AutoApplyResultingVouchers'}
                    />
                  </Box>
                </FormItemLayout>

                <FormItemLayout
                  bottomBorder
                  labelId="Force_discount"
                  descriptionId="Force_discount_description"
                  mdProportion="10x2"
                  noWrap
                  removeRootPaddings
                  smProportion="10x2"
                >
                  <Box className={classes.switch}>
                    <FkSwitch {...FIELDS['CampaignForceDiscount']} name={'ForceDiscount'} />
                  </Box>
                </FormItemLayout>

                {isLoyaltyMaxDiscountOn && (
                  <FormItemLayout
                    bottomBorder
                    descriptionId="Max_discount_amount_description"
                    labelId="Max_discount_amount"
                    mdProportion="8x4"
                    removeRootPaddings
                    showBetaTag
                  >
                    <FkNumeric {...FIELDS['CampaignMaxDiscount']} name={'MaxDiscount'} />
                  </FormItemLayout>
                )}
              </>
            )}
          </ModalContent>

          <ModalActions>
            <ActionButtonGroup
              onCancel={onCancel}
              editMode={editMode}
              onRemove={handleRemoveClick}
            />
          </ModalActions>
        </Form>
      </Modal>
      <YesNoDialog
        open={state.isRemoveDialogVisible}
        onYes={() => {
          setState({ ...state, isRemoveDialogVisible: false });
          onRemove(initialValues.CampaignId as number);
        }}
        onNo={() => setState({ ...state, isRemoveDialogVisible: false })}
        yesTextId={'Remove'}
        noTextId={'Cancel'}
        titleTextId={'Remove_retention_campaign'}
      >
        <Typography variant="body1" className={classes.removeDialog}>
          <Translate id="Pressing_remove_will_delete" />
        </Typography>
      </YesNoDialog>
    </>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state) => ({
  allStores: getStores(state),
  isLoyaltyMaxDiscountOn: flagService.isFlagOn(state, 'loyaltyMaxDiscount'),
  isSimplifiedRetentionCampaignOn: flagService.isFlagOn(state, 'simplifiedRetentionCampaign'),
  translate: getTranslate(state.locale),
});

export default compose<InnerProps, OuterProps>(
  connect(mapStateToProps, null),
  withFormik<Props, FormValues>({
    displayName: 'RetentionCampaignForm',
    enableReinitialize: true,
    mapPropsToValues: (props) => {
      return getDefaultFormState(props);
    },
    handleSubmit: async (values, formikBag) => {
      await formikBag.props.onCreate(values);
      formikBag.setSubmitting(false);
    },
  })
)(RetentionCampaignModal);
